import { useMemo, useState } from "react"
import { Box, Button, DialogContent, DialogTitle, Select, Stack, Typography } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { isEnabled } from "lib/@components/feature"
import { FEATURE_FLAG_OPTIMISER_CONDITIONAL_TRADES } from "../../feature-flag-conditional-trades"
import { FEATURE_FLAG_SCHEDULE_ASSIGNMENTS } from "../../feature-flag-schedule-assignments"
import { getCurrencySymbol } from "../format-utils"
import { VariantBreakdownTradeRow } from "./variant-breakdown-trade-row"
import { VariantBreakdownFacilityRow } from "./variant-breakdown-facility-row"
import { VariantBreakdownAssetTypeRow } from "./variant-breakdown-asset-type-row"
import { VariantBreakdownRuleImpactRow } from "./variant-breakdown-rule-impact-row"
import { VariantBreakdownTaskRow } from "./variant-breakdown-task-row"
import { VariantBreakdownServerTasks } from "./variant-breakdown-server-tasks"
import {
    buildResolutionMenuItems,
    RESOLUTION_FILTER_ANY,
    RESOLUTION_FILTER_ASSIGNMENT_ONLY,
    RESOLUTION_FILTER_CONDITIONAL_ONLY,
    RESOLUTION_FILTER_DEFAULT_ONLY,
} from "./resolution-filter-options"

const TASK_PAGE_SIZE = 100

export function VariantBreakdownDialog({ variant, scenarioId }) {
    const costResult = variant?.costResult
    const currencySymbol = getCurrencySymbol(costResult?.currency || variant?.rateCardCurrency)
    // Database-backed results carry no inline task array — task detail pages come
    // from the server, loaded only when "Show Tasks" opens.
    const serverTasksMode = !!costResult?.detailsRef?.setKey && !!scenarioId && !!variant?.id
    const [taskLimit, setTaskLimit] = useState(TASK_PAGE_SIZE)
    const [showTasks, setShowTasks] = useState(false)

    const trades = getSortedTrades()
    const facilities = getSortedFacilities()
    const assetTypes = getSortedAssetTypes()
    const ruleSetImpact = costResult?.ruleSetImpact ?? []
    // Wrap in useMemo so the fallback empty array has a stable identity across renders;
    // otherwise the downstream useMemo deps churn on every render.
    const allTasks = useMemo(computeAllTasks, [costResult])

    // Resolution filter: drives a client-side filter over the per-task list so the user
    // can isolate tasks routed by a conditional, by the default trade only, or by a
    // specific named conditional. Gated behind the feature flag so existing users see
    // an unchanged dialog.
    const conditionalTradesOn = isEnabled(FEATURE_FLAG_OPTIMISER_CONDITIONAL_TRADES)
    // FIQ-2457: the same "schedule assignment only" choice as the server-backed task list
    const scheduleAssignmentsOn = isEnabled(FEATURE_FLAG_SCHEDULE_ASSIGNMENTS)
    const [resolutionFilter, setResolutionFilter] = useState(RESOLUTION_FILTER_ANY)
    const conditionalNames = useMemo(collectConditionalNames, [allTasks])
    const filteredTasks = useMemo(applyResolutionFilter, [allTasks, resolutionFilter])
    const visibleTasks = filteredTasks.slice(0, taskLimit)
    const hasMoreTasks = taskLimit < filteredTasks.length

    return (
        <>
            <DialogTitle>{variant?.name} — Cost Breakdown</DialogTitle>
            <DialogContent sx={{ minWidth: 500 }}>
                <Stack spacing={3} sx={{ pt: 1 }}>
                    <If value={trades.length > 0}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                By Trade
                            </Typography>
                            <Box
                                sx={{
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    overflow: "hidden",
                                }}
                            >
                                <Repeat list={trades} item={<VariantBreakdownTradeRow currency={currencySymbol} />} />
                            </Box>
                        </Box>
                    </If>
                    <If value={facilities.length > 0}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                By Facility
                            </Typography>
                            <Box
                                sx={{
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    overflow: "hidden",
                                }}
                            >
                                <Repeat
                                    list={facilities}
                                    item={<VariantBreakdownFacilityRow currency={currencySymbol} />}
                                />
                            </Box>
                        </Box>
                    </If>
                    <If value={assetTypes.length > 0}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                By Asset Type
                            </Typography>
                            <Box
                                sx={{
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    overflow: "hidden",
                                }}
                            >
                                <Repeat
                                    list={assetTypes}
                                    item={<VariantBreakdownAssetTypeRow currency={currencySymbol} />}
                                />
                            </Box>
                        </Box>
                    </If>
                    <If value={ruleSetImpact.length > 0}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Rule Set Impact
                            </Typography>
                            <Box
                                sx={{
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    overflow: "hidden",
                                }}
                            >
                                <Repeat list={ruleSetImpact} item={<VariantBreakdownRuleImpactRow />} />
                            </Box>
                        </Box>
                    </If>
                    <If value={serverTasksMode}>
                        <VariantBreakdownServerTasks
                            variant={variant}
                            scenarioId={scenarioId}
                            currency={currencySymbol}
                        />
                    </If>
                    <If value={!serverTasksMode && allTasks.length > 0}>
                        <Box>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                <Typography variant="subtitle2">
                                    Task Detail ({filteredTasks.length} of {allTasks.length} tasks)
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <If value={conditionalTradesOn || scheduleAssignmentsOn}>
                                        <Select
                                            size="small"
                                            value={resolutionFilter}
                                            onChange={handleResolutionFilterChange}
                                            sx={{ minWidth: 200, fontSize: 13 }}
                                            data-cy="resolution-filter"
                                        >
                                            {buildResolutionMenuItems({
                                                conditionalTradesOn,
                                                scheduleAssignmentsOn,
                                                conditionalNames,
                                            })}
                                        </Select>
                                    </If>
                                    <Button
                                        size="small"
                                        onClick={handleToggleTasks}
                                        data-cy="variant-breakdown-toggle-tasks"
                                    >
                                        <If value={showTasks}>Hide Tasks</If>
                                        <If value={!showTasks}>Show Tasks</If>
                                    </Button>
                                </Stack>
                            </Stack>
                            <If value={showTasks}>
                                <Box
                                    sx={{
                                        border: 1,
                                        borderColor: "divider",
                                        borderRadius: 1,
                                        overflow: "hidden",
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        sx={{
                                            px: 2,
                                            py: 0.5,
                                            borderBottom: 1,
                                            borderColor: "divider",
                                            bgcolor: "action.hover",
                                        }}
                                    >
                                        <Box sx={{ flex: 3 }}>
                                            <Typography variant="caption" fontWeight={600}>
                                                Title
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="caption" fontWeight={600}>
                                                Trade
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="caption" fontWeight={600}>
                                                Facility
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: 1, textAlign: "right" }}>
                                            <Typography variant="caption" fontWeight={600}>
                                                Hours
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: 1, textAlign: "right" }}>
                                            <Typography variant="caption" fontWeight={600}>
                                                Cost
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    <Repeat
                                        list={visibleTasks}
                                        item={<VariantBreakdownTaskRow currency={currencySymbol} />}
                                    />
                                    <If value={hasMoreTasks}>
                                        <Box sx={{ p: 1, textAlign: "center" }}>
                                            <Button size="small" onClick={handleShowMore}>
                                                Show more ({filteredTasks.length - taskLimit} remaining)
                                            </Button>
                                        </Box>
                                    </If>
                                </Box>
                            </If>
                        </Box>
                    </If>
                </Stack>
            </DialogContent>
        </>
    )

    function getSortedTrades() {
        return [...(costResult?.tradeBreakdown || [])].sort((a, b) => (b.totalCost || 0) - (a.totalCost || 0))
    }

    function getSortedFacilities() {
        return [...(costResult?.facilityBreakdown || [])].sort((a, b) => (b.totalCost || 0) - (a.totalCost || 0))
    }

    function getSortedAssetTypes() {
        return [...(costResult?.assetTypeBreakdown || [])].sort((a, b) => (b.totalCost || 0) - (a.totalCost || 0))
    }

    function handleToggleTasks() {
        setShowTasks(!showTasks)
    }

    function handleShowMore() {
        setTaskLimit(taskLimit + TASK_PAGE_SIZE)
    }

    function handleResolutionFilterChange(e) {
        setResolutionFilter(e.target.value)
        setTaskLimit(TASK_PAGE_SIZE)
    }

    function computeAllTasks() {
        return costResult?.taskBreakdown ?? []
    }

    function collectConditionalNames() {
        const names = new Set()
        for (const task of allTasks) {
            const name = task.conditionMatched?.name
            if (name) names.add(name)
        }
        return [...names].sort()
    }

    function applyResolutionFilter() {
        if (resolutionFilter === RESOLUTION_FILTER_ANY) return allTasks
        // A task routed by a schedule assignment (FIQ-2457) was not routed by the default trade
        if (resolutionFilter === RESOLUTION_FILTER_DEFAULT_ONLY) {
            return allTasks.filter((t) => !t.conditionMatched && !t.assignmentMatched)
        }
        if (resolutionFilter === RESOLUTION_FILTER_CONDITIONAL_ONLY) return allTasks.filter((t) => !!t.conditionMatched)
        if (resolutionFilter === RESOLUTION_FILTER_ASSIGNMENT_ONLY) return allTasks.filter((t) => !!t.assignmentMatched)
        // Otherwise the filter is a specific conditional name.
        return allTasks.filter((t) => t.conditionMatched?.name === resolutionFilter)
    }
}
